import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight } from "@phosphor-icons/react";

const LOGO = "/logo-geekcreative.png";
// Bright, well-lit designer's workspace — high contrast for overlay text
const IMG = "https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=1600&q=90";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white text-[var(--gc-navy)] flex flex-col">
      <header className="border-b border-[var(--gc-border)] px-8 h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src={LOGO} alt="GeekCreative" className="h-16 md:h-20 w-auto block" />
        </Link>
        <div className="text-sm text-[var(--gc-text-muted)]">
          No account? <Link to="/register" className="link-underline text-[var(--gc-navy)] font-semibold" data-testid="login-register-link">Create one</Link>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        {/* Left panel: navy solid so overlay text is 100% legible */}
        <div className="hidden lg:flex flex-col justify-between relative overflow-hidden bg-[var(--gc-navy)] text-white p-14 xl:p-16">
          {/* Bright workspace image at low opacity, colour-toned to brand navy */}
          <img
            src={IMG}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-30"
            style={{ mixBlendMode: 'luminosity' }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--gc-navy)]/95 via-[var(--gc-navy)]/70 to-transparent" />

          <div className="relative">
            <div className="eyebrow mb-3 text-[var(--gc-orange)]">Welcome back</div>
          </div>

          <div className="relative">
            <div className="font-display text-5xl xl:text-6xl leading-[0.98] text-white">
              Track every pixel<br/>from brief<br/>to production.
            </div>
            <div className="mt-8 h-1 w-16 bg-[var(--gc-orange)]" />
            <p className="mt-8 text-white/80 max-w-sm text-sm leading-relaxed">
              Your projects, quotes, proofs and messages — all in one place.
            </p>
          </div>

          <div className="relative flex items-center gap-3 text-xs text-white/50 font-mono-tech uppercase tracking-widest">
            <span>GeekCreative</span>
            <span>·</span>
            <span>Kilrush, Co. Clare</span>
          </div>
        </div>

        {/* Right panel: form */}
        <div className="flex items-center justify-center p-8 lg:p-16">
          <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
            <div className="eyebrow mb-4">Access</div>
            <h1 className="font-display text-5xl md:text-6xl">Sign in.</h1>
            <p className="text-[var(--gc-text-muted)] mt-3">Enter your credentials to continue to the portal.</p>

            {err && <div className="mt-6 border-l-2 border-[var(--gc-danger)] bg-red-50 text-[var(--gc-danger)] p-3 text-sm" data-testid="login-error">{err}</div>}

            <div className="mt-10 space-y-8">
              <div>
                <div className="eyebrow mb-2">Email</div>
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                  className="gc-input" data-testid="login-email" placeholder="you@example.com" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="eyebrow">Password</div>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-semibold text-[var(--gc-orange)] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
                  className="gc-input" data-testid="login-password" placeholder="••••••••" />
              </div>
              <button type="submit" disabled={loading}
                className="btn-solid w-full justify-center disabled:opacity-50"
                data-testid="login-submit">
                {loading ? "Signing in…" : <>Sign in <ArrowUpRight size={16} weight="bold"/></>}
              </button>
              <div className="text-sm text-[var(--gc-text-muted)]">
                No account? <Link to="/register" className="link-underline text-[var(--gc-navy)] font-semibold">Create one</Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
